import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

export default function InformeIvaVerPage() {
  const { runId } = useParams();
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState(null);
  const [approving, setApproving] = useState(false);

  const loadRun = useCallback(async () => {
    const runRes = await api.get(`/informes-iva/run/${runId}?lite=1`);
    setRun(runRes.data?.run || null);
    return runRes.data?.run;
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [previewRes, runData] = await Promise.all([
          api.get(`/informes-iva/preview/${runId}`, { responseType: 'text' }),
          loadRun().catch(() => null)
        ]);
        if (!cancelled) {
          setHtml(previewRes.data);
          if (runData) setRun(runData);
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.error || 'No se pudo cargar el informe');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, loadRun]);

  const descargarPdf = async () => {
    try {
      const res = await api.get(`/informes-iva/pdf/${runId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-iva-${runId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo generar el PDF');
    }
  };

  const aprobar = async () => {
    setApproving(true);
    try {
      await api.post(`/informes-iva/approve/${runId}`);
      toast.success('Informe aprobado');
      await loadRun();
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo aprobar');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-600">
        Cargando informe…
      </div>
    );
  }

  if (!html) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <p>No se encontró el informe.</p>
        <Link to="/informes-iva" className="text-indigo-600 underline">
          Volver a Informes IVA
        </Link>
      </div>
    );
  }

  const pendienteAprobacion = run?.status === 'WAITING_APPROVAL';

  return (
    <div className="min-h-screen flex flex-col bg-slate-200">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-2">
        <Link to="/informes-iva" className="text-sm text-slate-600 hover:text-slate-900 shrink-0">
          ← Panel del mes
        </Link>
        {pendienteAprobacion && (
          <span className="text-xs font-medium text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-2 py-1">
            Pendiente de aprobación
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {pendienteAprobacion && (
            <button
              type="button"
              disabled={approving}
              onClick={aprobar}
              className="px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {approving ? 'Aprobando…' : 'Aprobar informe'}
            </button>
          )}
          <button
            type="button"
            onClick={descargarPdf}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Descargar PDF
          </button>
        </div>
      </div>
      <iframe
        title="Informe IVA"
        srcDoc={html}
        className="flex-1 w-full min-h-[calc(100vh-56px)] bg-white border-0"
        sandbox="allow-same-origin"
      />
    </div>
  );
}
